import "./add-to-cart/add-to-cart";
import "./conditional-details/conditional-details";
import "./video/video";
import "./tab/tab";
import "./price-snippet/price-snippet";
import "./trap-focus/trap-focus";
import "./localization-form/localization-form";
import "./aria-listbox/aria-listbox";
import "./cart/cart";
import "./quantity-input/quantity-input";
import "./markup-modal/markup-modal";
import "./load-in-view/load-in-view";
import "./play-in-view-video/play-in-view-video";
import "./product-card/product-card";
import "./waitlist/waitlist";
import "./find-in-store/find-in-store";
import "./wishlist/wishlist";
import "./relative-link/relative-link";
import "./gorgias-chat/gorgias-chat";

import Swiper from "swiper";
import {
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
  Mousewheel,
  Scrollbar,
  Thumbs,
  EffectCreative,
  Zoom,
} from "swiper/modules";

// Load Swiper once for re-use by any other components
window.loadSwiperFulfillPromise({
  Swiper,
  Autoplay,
  EffectFade,
  EffectCreative,
  Navigation,
  Pagination,
  Mousewheel,
  Scrollbar,
  Thumbs,
  Zoom,
});

/**
 * Scrapes a section element for data needing to be handled in section JS
 *
 * @param {DOMNode} container
 * @returns parsed JSON
 */
window.scrapeSectionData = (container) => {
  const dataEl = container.querySelector("[data-section-data]");
  if (!dataEl) return {};

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    console.warn(`Error parsing data for`, dataEl);
    return {};
  }
};

window.productUrl = (handle) => {
  return `${window.routes.root_url === "/" ? "" : window.routes.root_url}/products/${handle}`;
};

/**
 * Formats a decimal amount as currency in the visitor's own locale.
 *
 * @param {number} num Amount in major units (e.g. 19.99, not 1999)
 * @param {boolean} showTrailingZeros Force two decimal places
 * @param {string} currency ISO 4217 currency code
 */
window.formatMoney = (num, showTrailingZeros, currency = "USD") => {
  const fractionDigits = showTrailingZeros
    ? 2
    : (num * 100) % 100 === 0
      ? 0
      : 2;

  // `undefined` (not "") is what Intl accepts to mean "use the runtime locale".
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

// Resize Shopify Image URL
window.getSizedImageUrl = (src, size) => {
  // The `_400x400.jpg` filename convention is legacy and no longer resolves on
  // the Shopify CDN; sizing is done with `width`/`height` query parameters.
  // `size` keeps the same "400x400" / "400x" shape as before.
  if (src) {
    const keys = ["width", "height"];
    const params = size
      .split("x")
      .map((value, i) => ({
        key: keys[i],
        value,
      }))
      .filter((param) => param.value)
      .map(({ key, value }) => `${key}=${value}`)
      .join("&");

    return `${src}${src.includes("?") ? "&" : "?"}${params}`;
  } else {
    return "";
  }
};

const lockers = new Set();

// `overflow: hidden` on the root has stopped touch scrolling since iOS 16.
// Pinning `body` with `position: fixed` instead relayouts and recomposites a
// long grid on every open and close, and toggles Safari's toolbar, firing
// `resize` — suspected of crashing Safari on lower-memory iPhones.
window.lockScroll = (locker) => {
  lockers.add(locker);
  document.documentElement.style.overflow = "hidden";
};

window.unlockScroll = (locker) => {
  if (!lockers.delete(locker) || lockers.size) return;

  document.documentElement.style.overflow = null;
};

// iOS Safari mis-tracks `visualViewport` after the on-screen keyboard opens
// and closes, leaving `position: fixed`/`sticky` elements (e.g. the sticky
// header) nudged out of place. Once the keyboard fully closes, force a 1px
// scroll nudge so Safari recomputes the viewport.
if (window.visualViewport) {
  let keyboardWasOpen = false;

  window.visualViewport.addEventListener("resize", () => {
    const keyboardIsOpen =
      window.visualViewport.height < window.innerHeight * 0.75;

    if (keyboardWasOpen && !keyboardIsOpen) {
      window.setTimeout(() => {
        if (window.visualViewport.offsetTop !== 0) {
          window.scrollBy(0, -1);
          window.scrollBy(0, 1);
        }
      }, 100);
    }

    keyboardWasOpen = keyboardIsOpen;
  });
}

// Below header dimmer (for mega menus, search, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".below-header-dimmer");

  window.openDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Full screen dimmer (for drawers, etc)
(() => {
  const openers = new Set();
  const darkOpeners = new Set();

  const dimmer = document.querySelector(".full-screen-dimmer");

  /**
   * @param {object} [options]
   * @param {boolean} [options.dark] - Use the darker overlay while this opener is open.
   */
  window.openFullDimmer = (opener, { dark = false } = {}) => {
    openers.add(opener);
    if (dark) darkOpeners.add(opener);

    dimmer.classList.toggle("dimmer--dark", darkOpeners.size > 0);
    dimmer.classList.add("dimmer--active");
  };

  window.closeFullDimmer = (opener) => {
    openers.delete(opener);
    darkOpeners.delete(opener);

    dimmer.classList.toggle("dimmer--dark", darkOpeners.size > 0);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Skip link: move focus to the first interactive element inside #MainContent.
// Paired with `.main-skip-link` in base/_global.scss.
(() => {
  const focusFirstElement = () => {
    const main = document.querySelector("#MainContent");
    const firstEl = main?.querySelector("a,button,input");

    if (firstEl) {
      firstEl.focus();
    }
  };

  const url = new URL(window.location);

  if (url.hash === "#MainContent") {
    focusFirstElement();
  }

  document.addEventListener("click", (e) => {
    const skipLink = e.target.closest("[data-skip-link]");
    if (skipLink) {
      e.preventDefault();
      focusFirstElement();
    }
  });
})();

window.idToGid = (id, type) => {
  return `gid://shopify/${type}/${id}`;
};

window.gidToId = (id) => {
  return id.replace(/^\D*/, "");
};

// Breakpoints. This map is duplicated in two other places — keep all three in
// sync when changing a value or adding a key:
//   1. $breakpoints                 — components/global/base/_helpers.scss
//   2. window.themeBreakpoints      — components/global/global.js (here)
//   3. the `case break_m` statement — components/global/snippet.image.liquid
window.themeBreakpoints = {
  phone: 640,
  tablet: 768,
  "tablet-wide": 1024,
  "desktop-small": 1280,
  desktop: 1440,
  "desktop-large": 1600,
  "desktop-ultra-large": 1920,
};

// JS translation handler. Interpolates {{ variable }} placeholders in strings
// passed through from Liquid.
window.theme = window.theme || {};
window.theme.translations = function (stringObject, key, variables = {}) {
  let str = stringObject[key];

  if (!str) return "";

  return str.replace(/{{\s*(\w+)\s*}}/g, (match, varName) => {
    return variables[varName] !== undefined ? variables[varName] : "";
  });
};
